/**
 * The amenity utilization report (P2, #642): how busy each amenity was
 * over a range, and when. Pure helpers, unit-tested.
 *
 * The heatmap is booked hours by local weekday (rows, 0 = Sunday) and
 * hour of day (columns), as the server sends it — seven rows of 24 —
 * shaded in five steps relative to the busiest cell, so a quiet amenity's
 * busiest hour still stands out against its quiet ones.
 */
import { ReservationUtilizationAmenity } from '../clients/rust-api-client';
import { addDays } from './reservationMeta';

export type Grid = number[][];

/** A 7×24 grid of zeros. */
export const emptyGrid = (): Grid => Array.from({ length: 7 }, () => Array(24).fill(0));

/** Cell-by-cell sum of several grids — the heatmap for "all amenities". Ragged input is padded. */
export const sumGrids = (grids: Grid[]): Grid => {
  const out = emptyGrid();
  for (const g of grids) {
    for (let d = 0; d < 7; d += 1) {
      for (let h = 0; h < 24; h += 1) out[d][h] += g[d]?.[h] ?? 0;
    }
  }
  return out;
};

/** 0 for an empty cell, else 1–4 by share of the busiest cell. */
export const heatLevel = (value: number, max: number): number => {
  if (value <= 0 || max <= 0) return 0;
  return Math.min(4, Math.max(1, Math.ceil((value / max) * 4)));
};

export const gridMax = (g: Grid): number => Math.max(0, ...g.flat());

/**
 * The hours worth drawing: from the first hour anything was booked to
 * the last, widened to at least 8 AM – 8 PM so a sparse grid still reads
 * as a day. [start, end) in hours.
 */
export const busyHours = (g: Grid): { start: number; end: number } => {
  let start = 8;
  let end = 20;
  for (let h = 0; h < 24; h += 1) {
    if (g.some((row) => (row[h] ?? 0) > 0)) {
      start = Math.min(start, h);
      end = Math.max(end, h + 1);
    }
  }
  return { start, end };
};

/** '42%', or '—' when the amenity had no open hours in the range. */
export const formatPercent = (p: number | null | undefined): string =>
  p === null || p === undefined ? '—' : `${Math.round(p)}%`;

/** '12.5 h' (one decimal at most). */
export const formatHours = (h: number): string =>
  `${Number.isInteger(h) ? h : Math.round(h * 10) / 10} h`;

/** Bookings that took place: approved (still to happen in range) plus completed. */
export const bookingsHeld = (a: ReservationUtilizationAmenity): number =>
  a.bookings.approved + a.bookings.completed;

export interface UtilizationTotals {
  bookings: number;
  bookedHours: number;
  openHours: number;
  noShows: number;
  cancellationsEarly: number;
  cancellationsLate: number;
  /** Decimal string, summed in cents so no float creeps into money. */
  feeRevenue: string;
}

const toCents = (money: string): number => {
  const m = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(money.trim());
  if (!m) return 0;
  const cents = Number(m[2]) * 100 + Number((m[3] ?? '').padEnd(2, '0') || '0');
  return m[1] === '-' ? -cents : cents;
};

const fromCents = (cents: number): string => {
  const sign = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
};

export const totalsOf = (rows: ReservationUtilizationAmenity[]): UtilizationTotals => ({
  bookings: rows.reduce((n, a) => n + bookingsHeld(a), 0),
  bookedHours: rows.reduce((n, a) => n + a.booked_hours, 0),
  openHours: rows.reduce((n, a) => n + a.open_hours, 0),
  noShows: rows.reduce((n, a) => n + a.no_shows, 0),
  cancellationsEarly: rows.reduce((n, a) => n + a.cancellations_early, 0),
  cancellationsLate: rows.reduce((n, a) => n + a.cancellations_late, 0),
  feeRevenue: fromCents(rows.reduce((n, a) => n + toCents(a.fee_revenue), 0)),
});

export type RangePreset = 'last30' | 'last90' | 'thisMonth' | 'lastMonth';

export const RANGE_LABELS: Record<RangePreset, string> = {
  last30: 'Last 30 days',
  last90: 'Last 90 days',
  thisMonth: 'This month',
  lastMonth: 'Last month',
};

/** [from, to] (inclusive days) for a preset, relative to `today` ('YYYY-MM-DD'). */
export const presetRange = (preset: RangePreset, today: string): { from: string; to: string } => {
  const [y, m] = today.split('-').map(Number);
  const pad = (n: number) => String(n).padStart(2, '0');
  const monthStart = `${y}-${pad(m)}-01`;
  switch (preset) {
    case 'last30':
      return { from: addDays(today, -29), to: today };
    case 'last90':
      return { from: addDays(today, -89), to: today };
    case 'thisMonth':
      return { from: monthStart, to: today };
    case 'lastMonth': {
      const lastDay = addDays(monthStart, -1);
      return { from: `${lastDay.slice(0, 7)}-01`, to: lastDay };
    }
  }
};
